'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { chartsAPI } from '../services/api';
import {
  Bookmark,
  BarChart3,
  TrendingUp,
  PieChart,
  AreaChart,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Copy,
  Calendar,
  Tag,
  Database,
  Sparkles,
  Target,
  Table,
  Activity,
  Zap,
  Loader2,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Maximize2,
  Download,
  AlertCircle,
  Heart,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart as RechartsAreaChart,
  Area,
  ScatterChart,
  Scatter,
  ComposedChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface Chart {
  id: string;
  name: string;
  type: string;
  indexName: string;
  description?: string;
  dataConfig?: {
    xAxis?: { field: string; label?: string };
    yAxis?: { field: string; label?: string; aggregation?: string };
    series?: Array<{ field: string; label?: string }>;
    unit?: string;
    seriesToUnitMap?: Record<string, string>;
  };
  metadata?: {
    tags?: string[];
    isPublic?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
}

interface ChartData {
  chart: Chart;
  chartData: any[];
  loading: boolean;
  error: string | null;
  totalResults?: number;
}

const MyCharts: React.FC = () => {
  const navigate = useNavigate();
  const [charts, setCharts] = useState<Chart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [chartsData, setChartsData] = useState<Map<string, ChartData>>(new Map());
  const [selectedChartId, setSelectedChartId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchCharts();
  }, []);

  // Ne plus charger automatiquement les données - seulement à la demande dans le modal
  // useEffect(() => {
  //   charts.forEach((chart) => {
  //     if (!chartsData.has(chart.id)) {
  //       fetchChartData(chart);
  //     }
  //   });
  // }, [charts]);

  const fetchCharts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await chartsAPI.getCharts(true);
      if (response.data.success) {
        setCharts(response.data.data || []);
      } else {
        setError(response.data.message || 'Erreur lors du chargement des graphiques');
      }
    } catch (err: any) {
      console.error('Error fetching charts:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des graphiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async (chart: Chart) => {
    const chartDataState: ChartData = {
      chart,
      chartData: [],
      loading: true,
      error: null,
    };
    setChartsData(new Map(chartsData.set(chart.id, chartDataState)));

    try {
      const response = await chartsAPI.getChartData(chart.id);
      console.log(`[MyCharts] Chart ${chart.id} response:`, response.data);
      
      if (response.data.success) {
        const data = response.data.data;
        const chartData = data.chartData || {};
        const config = data.config || {};
        const rawAggregations = data.rawAggregations || {};
        
        console.log(`[MyCharts] Chart ${chart.id} ========== FULL RESPONSE ==========`);
        console.log(`[MyCharts] Chart ${chart.id} config:`, JSON.stringify(config, null, 2));
        console.log(`[MyCharts] Chart ${chart.id} config.filters:`, JSON.stringify(config.filters, null, 2));
        console.log(`[MyCharts] Chart ${chart.id} config.dataConfig:`, JSON.stringify(config.dataConfig, null, 2));
        console.log(`[MyCharts] Chart ${chart.id} rawAggregations:`, JSON.stringify(rawAggregations, null, 2));
        console.log(`[MyCharts] Chart ${chart.id} rawAggregations.main:`, rawAggregations.main);
        console.log(`[MyCharts] Chart ${chart.id} rawAggregations.main?.buckets:`, rawAggregations.main?.buckets);
        console.log(`[MyCharts] Chart ${chart.id} rawAggregations.main?.buckets?.length:`, rawAggregations.main?.buckets?.length);
        console.log(`[MyCharts] Chart ${chart.id} raw chartData:`, JSON.stringify(chartData, null, 2));
        console.log(`[MyCharts] Chart ${chart.id} chartData.labels:`, chartData.labels);
        console.log(`[MyCharts] Chart ${chart.id} chartData.datasets:`, chartData.datasets);
        console.log(`[MyCharts] Chart ${chart.id} chartData type:`, typeof chartData, Array.isArray(chartData));
        
        // Convertir le format Chart.js (labels/datasets) en format Recharts si nécessaire
        let formattedData: any[] = [];
        
        if (Array.isArray(chartData)) {
          formattedData = chartData;
          console.log(`[MyCharts] Chart ${chart.id} chartData is already array`);
        } else if (chartData && typeof chartData === 'object') {
          // Format Chart.js: convertir en format Recharts
          if (chartData.labels && Array.isArray(chartData.labels)) {
            console.log(`[MyCharts] Chart ${chart.id} Converting Chart.js format, labels: ${chartData.labels.length}, datasets: ${chartData.datasets?.length || 0}`);
            console.log(`[MyCharts] Chart ${chart.id} First dataset:`, chartData.datasets?.[0]);
            console.log(`[MyCharts] Chart ${chart.id} First dataset.data:`, chartData.datasets?.[0]?.data);
            console.log(`[MyCharts] Chart ${chart.id} First dataset.data type:`, typeof chartData.datasets?.[0]?.data, Array.isArray(chartData.datasets?.[0]?.data));
            console.log(`[MyCharts] Chart ${chart.id} First dataset.data length:`, chartData.datasets?.[0]?.data?.length);
            if (chartData.datasets?.[0]?.data && chartData.datasets[0].data.length > 0) {
              console.log(`[MyCharts] Chart ${chart.id} First 5 values from dataset.data:`, chartData.datasets[0].data.slice(0, 5));
            }
            
            formattedData = chartData.labels.map((label: string, idx: number) => {
              const item: any = { name: String(label), fullName: String(label) };
              if (chartData.datasets && Array.isArray(chartData.datasets)) {
                chartData.datasets.forEach((dataset: any, dIdx: number) => {
                  // Vérifier que dataset.data existe et est un tableau
                  if (dataset.data && Array.isArray(dataset.data) && dataset.data.length > idx) {
                    const value = dataset.data[idx];
                    // Utiliser ?? 0 pour gérer null/undefined, mais garder 0 si c'est vraiment 0
                    const finalValue = (value !== null && value !== undefined) ? value : 0;
                    const seriesKey = dataset.label || `Series ${dIdx + 1}`;
                    item[seriesKey] = finalValue;
                    // Si c'est la première série, utiliser aussi "value" pour compatibilité
                    if (dIdx === 0) {
                      item.value = finalValue;
                      // Aussi utiliser "Value" avec majuscule pour compatibilité
                      item.Value = finalValue;
                    }
                  } else {
                    console.warn(`[MyCharts] Chart ${chart.id} Dataset ${dIdx} data missing or invalid at index ${idx}`);
                    const seriesKey = dataset.label || `Series ${dIdx + 1}`;
                    item[seriesKey] = 0;
                    if (dIdx === 0) {
                      item.value = 0;
                      item.Value = 0;
                    }
                  }
                });
              }
              return item;
            });
          } else if (chartData.tableData && Array.isArray(chartData.tableData)) {
            // Format table
            console.log(`[MyCharts] Chart ${chart.id} Using tableData format`);
            formattedData = chartData.tableData;
          } else {
            // Essayer de trouver des données dans d'autres propriétés
            console.warn(`[MyCharts] Chart ${chart.id} Unknown chartData format, keys:`, Object.keys(chartData));
            // Essayer de convertir l'objet en tableau
            const arrayValue = Object.values(chartData).find(v => Array.isArray(v));
            formattedData = arrayValue ? (arrayValue as any[]) : [];
          }
        }
        
        console.log(`[MyCharts] Chart ${chart.id} formatted data (first 3):`, formattedData.slice(0, 3));
        console.log(`[MyCharts] Chart ${chart.id} formatted data length:`, formattedData.length);
        
        setChartsData(
          new Map(
            chartsData.set(chart.id, {
              chart,
              chartData: formattedData,
              loading: false,
              error: null,
              totalResults: data.rawAggregations?.total_count?.value || 0,
            })
          )
        );
      } else {
        console.error(`[MyCharts] Chart ${chart.id} error:`, response.data.message);
        setChartsData(
          new Map(
            chartsData.set(chart.id, {
              chart,
              chartData: [],
              loading: false,
              error: response.data.message || 'Error loading data',
            })
          )
        );
      }
    } catch (err: any) {
      console.error(`[MyCharts] Chart ${chart.id} exception:`, err);
      setChartsData(
        new Map(
          chartsData.set(chart.id, {
            chart,
            chartData: [],
            loading: false,
            error: err.response?.data?.message || 'Error loading data',
          })
        )
      );
    }
  };

  const handleViewChart = async (chartId: string) => {
    setSelectedChartId(chartId);
    setIsModalOpen(true);
    
    // Charger les données si elles ne sont pas déjà chargées
    const chart = charts.find(c => c.id === chartId);
    if (chart && !chartsData.has(chartId)) {
      await fetchChartData(chart);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedChartId(null);
  };

  const handleEditChart = (chartId: string) => {
    navigate(`/charts/${chartId}/edit`);
  };

  const handleDeleteChart = async (chartId: string) => {
    if (!window.confirm('Are you sure you want to delete this chart?')) {
      return;
    }

    try {
      await chartsAPI.deleteChart(chartId);
      await fetchCharts();
      const newChartsData = new Map(chartsData);
      newChartsData.delete(chartId);
      setChartsData(newChartsData);
    } catch (err: any) {
      console.error('Error deleting chart:', err);
      alert('Erreur lors de la suppression du graphique');
    }
  };

  const handleDuplicateChart = async (chartId: string, chartName: string) => {
    try {
      await chartsAPI.duplicateChart(chartId, `${chartName} (Copie)`);
      await fetchCharts();
    } catch (err: any) {
      console.error('Error duplicating chart:', err);
      alert('Error duplicating chart');
    }
  };

  const getChartTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      bar: 'Bars',
      line: 'Lines',
      pie: 'Pie',
      doughnut: 'Donut',
      area: 'Area',
      scatter: 'Scatter',
      radar: 'Radar',
      combo: 'Combo',
      table: 'Table',
    };
    return labels[type] || type;
  };

  const getChartIcon = (type: string) => {
    switch (type) {
      case 'bar':
        return <BarChart3 className="w-6 h-6" />;
      case 'line':
        return <TrendingUp className="w-6 h-6" />;
      case 'pie':
      case 'doughnut':
        return <PieChart className="w-6 h-6" />;
      case 'area':
        return <AreaChart className="w-6 h-6" />;
      case 'scatter':
        return <Target className="w-6 h-6" />;
      case 'radar':
        return <Activity className="w-6 h-6" />;
      case 'table':
        return <Table className="w-6 h-6" />;
      default:
        return <BarChart3 className="w-6 h-6" />;
    }
  };

  const getChartColor = (type: string) => {
    const colors: { [key: string]: string } = {
      bar: 'from-blue-500 to-blue-600',
      line: 'from-green-500 to-green-600',
      pie: 'from-purple-500 to-purple-600',
      doughnut: 'from-pink-500 to-pink-600',
      area: 'from-indigo-500 to-indigo-600',
      scatter: 'from-orange-500 to-orange-600',
      radar: 'from-cyan-500 to-cyan-600',
      combo: 'from-violet-500 to-violet-600',
      table: 'from-gray-500 to-gray-600',
    };
    return colors[type] || 'from-cyan-500 to-cyan-600';
  };

  // Générer des données de démo pour les aperçus
  const generateDemoData = (type: string) => {
    const baseData = [
      { name: 'Jan', value: 45 },
      { name: 'Feb', value: 52 },
      { name: 'Mar', value: 48 },
      { name: 'Apr', value: 61 },
      { name: 'May', value: 55 },
      { name: 'Jun', value: 67 },
    ];

    switch (type) {
      case 'bar':
        return baseData.map((d, i) => ({ ...d, value: 30 + i * 8 + Math.random() * 10 }));
      case 'line':
        return baseData.map((d, i) => ({ ...d, value: 40 + i * 5 + Math.sin(i) * 10 }));
      case 'area':
        return baseData.map((d, i) => ({ ...d, value: 35 + i * 6 + Math.cos(i) * 8 }));
      case 'pie':
      case 'doughnut':
        return [
          { name: 'A', value: 35 },
          { name: 'B', value: 25 },
          { name: 'C', value: 20 },
          { name: 'D', value: 20 },
        ];
      default:
        return baseData;
    }
  };

  // Générer un aperçu de graphique réaliste avec Recharts
  const renderChartPreview = (chart: Chart) => {
    const demoData = generateDemoData(chart.type);
    const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#ef4444'];

    switch (chart.type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={demoData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <Bar dataKey="value" fill="#06b6d4" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={demoData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: '#10b981', r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
      case 'doughnut':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={demoData}
                cx="50%"
                cy="50%"
                innerRadius={chart.type === 'doughnut' ? 30 : 0}
                outerRadius={50}
                dataKey="value"
              >
                {demoData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
            </RechartsPieChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsAreaChart data={demoData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke="#8b5cf6" 
                fill="#8b5cf6" 
                fillOpacity={0.6}
              />
            </RechartsAreaChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <div className="flex items-center justify-center h-full opacity-60">
            {getChartIcon(chart.type)}
          </div>
        );
    }
  };

  // Get category from tags or index
  const getCategory = (chart: Chart) => {
    if (chart.metadata?.tags && chart.metadata.tags.length > 0) {
      const categoryTags = ['Finance', 'Users', 'Sales', 'Marketing', 'Analytics', 'Analysis'];
      const foundTag = chart.metadata.tags.find(tag => 
        categoryTags.some(cat => tag.toLowerCase().includes(cat.toLowerCase()))
      );
      if (foundTag) {
        return categoryTags.find(cat => foundTag.toLowerCase().includes(cat.toLowerCase())) || foundTag;
      }
      return chart.metadata.tags[0];
    }
    const indexName = chart.indexName.toLowerCase();
    if (indexName.includes('finance') || indexName.includes('revenue')) return 'Finance';
    if (indexName.includes('user') || indexName.includes('utilisateur')) return 'Users';
    if (indexName.includes('sale') || indexName.includes('vente')) return 'Sales';
    if (indexName.includes('marketing') || indexName.includes('campaign')) return 'Marketing';
    if (indexName.includes('analytics') || indexName.includes('analytique')) return 'Analytics';
    return 'Analysis';
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };

  const renderChart = (chart: Chart, chartData: any) => {
    const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

    console.log(`[MyCharts renderChart] Chart ${chart.id}, type: ${chart.type}`);
    console.log(`[MyCharts renderChart] chartData type:`, typeof chartData, Array.isArray(chartData));
    console.log(`[MyCharts renderChart] chartData:`, chartData);

    // S'assurer que chartData est un tableau
    let dataArray: any[] = [];
    if (Array.isArray(chartData)) {
      dataArray = chartData;
      console.log(`[MyCharts renderChart] ChartData is array, length: ${dataArray.length}`);
    } else if (chartData && typeof chartData === 'object') {
      // Si c'est un objet avec une structure Chart.js (labels/datasets)
      if (chartData.labels && Array.isArray(chartData.labels)) {
        console.log(`[MyCharts renderChart] Converting Chart.js format`);
        console.log(`[MyCharts renderChart] Labels:`, chartData.labels);
        console.log(`[MyCharts renderChart] Datasets:`, chartData.datasets);
        
        // Convertir le format Chart.js en format Recharts
        console.log(`[MyCharts renderChart] Dataset structure:`, chartData.datasets?.[0]);
        console.log(`[MyCharts renderChart] Dataset.data:`, chartData.datasets?.[0]?.data);
        console.log(`[MyCharts renderChart] Dataset.data type:`, typeof chartData.datasets?.[0]?.data, Array.isArray(chartData.datasets?.[0]?.data));
        
        dataArray = chartData.labels.map((label: string, idx: number) => {
          const item: any = { name: String(label), fullName: String(label) };
          if (chartData.datasets && Array.isArray(chartData.datasets)) {
            chartData.datasets.forEach((dataset: any, dIdx: number) => {
              // Vérifier que dataset.data existe et est un tableau
              if (dataset.data && Array.isArray(dataset.data) && dataset.data.length > idx) {
                const value = dataset.data[idx];
                // Utiliser la valeur même si c'est 0
                const finalValue = (value !== null && value !== undefined) ? value : 0;
                const seriesKey = dataset.label || `Series ${dIdx + 1}`;
                item[seriesKey] = finalValue;
                // Si c'est la première série, utiliser aussi "value" pour compatibilité
                if (dIdx === 0) {
                  item.value = finalValue;
                  // Aussi utiliser "Value" avec majuscule pour compatibilité
                  item.Value = finalValue;
                }
              } else {
                console.warn(`[MyCharts renderChart] Dataset ${dIdx} data missing or invalid at index ${idx}`);
                const seriesKey = dataset.label || `Series ${dIdx + 1}`;
                item[seriesKey] = 0;
                if (dIdx === 0) {
                  item.value = 0;
                  item.Value = 0;
                }
              }
            });
          }
          return item;
        });
        console.log(`[MyCharts renderChart] Converted dataArray (first 3):`, dataArray.slice(0, 3));
        console.log(`[MyCharts renderChart] Converted dataArray length:`, dataArray.length);
      } else {
        // Essayer de convertir l'objet en tableau
        console.log(`[MyCharts renderChart] Trying to convert object to array, keys:`, Object.keys(chartData));
        // Chercher un tableau dans les valeurs
        const arrayValue = Object.values(chartData).find(v => Array.isArray(v));
        if (arrayValue) {
          dataArray = arrayValue as any[];
          console.log(`[MyCharts renderChart] Found array in object, length:`, dataArray.length);
        } else {
          dataArray = [];
          console.warn(`[MyCharts renderChart] No array found in object`);
        }
      }
    } else {
      console.warn(`[MyCharts renderChart] Unexpected chartData type:`, typeof chartData, chartData);
    }

    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      console.warn(`[MyCharts renderChart] No data to display, dataArray:`, dataArray);
      return (
        <div className="flex items-center justify-center h-full bg-white rounded-lg border border-gray-200">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 text-sm">No data available</p>
            <p className="text-gray-500 text-xs mt-2">
              {chartData ? `Type: ${typeof chartData}` : 'Empty data'}
            </p>
          </div>
        </div>
      );
    }

    // Détecter si on a des séries multiples
    // Exclure 'name', 'fullName', 'value' et 'Value' de la détection (car 'value' et 'Value' sont des alias)
    const seriesKeys = dataArray.length > 0 && dataArray[0]
      ? Object.keys(dataArray[0]).filter(k => {
          const lowerKey = k.toLowerCase();
          return k !== 'name' && k !== 'fullName' && lowerKey !== 'value';
        })
      : [];
    const hasMultipleSeries = seriesKeys.length > 1;
    
    // Si pas de séries multiples mais qu'on a 'value' ou 'Value', les utiliser
    const valueKey = dataArray.length > 0 && dataArray[0]
      ? (Object.keys(dataArray[0]).find(k => k.toLowerCase() === 'value') || 'value')
      : 'value';
    
    // Unités sauvegardées (depuis Search)
    const savedUnit = chart.dataConfig?.unit || '';
    const savedSeriesToUnitMap = chart.dataConfig?.seriesToUnitMap || {};
    const hasMixedUnits = hasMultipleSeries && Object.keys(savedSeriesToUnitMap).length > 1;
    const yAxisLabel = hasMixedUnits
      ? (chart.dataConfig?.yAxis?.label || chart.dataConfig?.yAxis?.field || 'Value')
      : (savedUnit ? `${chart.dataConfig?.yAxis?.label || chart.dataConfig?.yAxis?.field || 'Value'} (${savedUnit})` : (chart.dataConfig?.yAxis?.label || chart.dataConfig?.yAxis?.field || 'Value'));
    const tooltipFormatter = (value: any, name?: any) => {
      if (value == null) return '';
      const seriesKey = typeof name === 'string' ? name : String(name ?? '');
      const unit = (seriesKey && savedSeriesToUnitMap[seriesKey]) || savedUnit;
      return `${Number(value).toLocaleString()}${unit ? ` ${unit}` : ''}`;
    };
    const getSeriesDisplayName = (key: string) => savedSeriesToUnitMap[key] ? `${key} (${savedSeriesToUnitMap[key]})` : key;
    
    console.log(`[MyCharts renderChart] Series keys:`, seriesKeys);
    console.log(`[MyCharts renderChart] Has multiple series:`, hasMultipleSeries);
    console.log(`[MyCharts renderChart] Value key:`, valueKey);
    console.log(`[MyCharts renderChart] First item keys:`, dataArray.length > 0 ? Object.keys(dataArray[0]) : []);
    console.log(`[MyCharts renderChart] First item values:`, dataArray.length > 0 ? dataArray[0] : {});

    switch (chart.type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dataArray} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              {hasMultipleSeries ? (
                seriesKeys.map((key, index) => (
                  <Bar key={key} dataKey={key} fill={COLORS[index % COLORS.length]} name={getSeriesDisplayName(key)} />
                ))
              ) : (
                <Bar dataKey={valueKey} fill={COLORS[0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dataArray} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              {hasMultipleSeries ? (
                seriesKeys.map((key, index) => (
                  <Line key={key} type="monotone" dataKey={key} stroke={COLORS[index % COLORS.length]} name={getSeriesDisplayName(key)} />
                ))
              ) : (
                <Line type="monotone" dataKey={valueKey} stroke={COLORS[0]} />
              )}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsAreaChart data={dataArray} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              {hasMultipleSeries ? (
                seriesKeys.map((key, index) => (
                  <Area key={key} type="monotone" dataKey={key} stroke={COLORS[index % COLORS.length]} fill={COLORS[index % COLORS.length]} fillOpacity={0.6} name={getSeriesDisplayName(key)} />
                ))
              ) : (
                <Area type="monotone" dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.6} />
              )}
            </RechartsAreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPieChart>
              <Pie
                data={dataArray}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => {
                  const total = dataArray.reduce((sum: number, item: any) => sum + (item.value || 0), 0);
                  const percent = total > 0 ? ((entry.value || 0) / total) * 100 : 0;
                  return `${entry.name || ''}: ${percent.toFixed(0)}%`;
                }}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {dataArray.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any) => value != null ? `${Number(value).toLocaleString()}${savedUnit ? ` ${savedUnit}` : ''}` : ''} />
              <Legend />
            </RechartsPieChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        const scatterYLabel = savedUnit ? `${chart.dataConfig?.yAxis?.field || 'Y'} (${savedUnit})` : (chart.dataConfig?.yAxis?.field || 'Y');
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart data={dataArray} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="x" name={chart.dataConfig?.xAxis?.field} tick={{ fontSize: 11 }} />
              <YAxis dataKey="y" name={scatterYLabel} tick={{ fontSize: 12 }} label={{ value: scatterYLabel, angle: -90, position: 'insideLeft' }} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={tooltipFormatter} />
              <Legend />
              <Scatter name={`${chart.dataConfig?.xAxis?.field || 'X'} vs ${scatterYLabel}`} dataKey="y" fill={COLORS[0]} />
            </ScatterChart>
          </ResponsiveContainer>
        );

      case 'radar':
        const radarData = dataArray.map((item: any) => ({
          subject: item.name,
          value: item.value || 0,
        }));
        const radarName = savedUnit ? `${chart.dataConfig?.yAxis?.field || 'Value'} (${savedUnit})` : (chart.dataConfig?.yAxis?.field || 'Value');
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis angle={90} domain={[0, 'auto']} tick={{ fontSize: 10 }} />
              <Radar name={radarName} dataKey="value" stroke={COLORS[0]} fill={COLORS[0]} fillOpacity={0.6} />
              <Legend />
              <Tooltip formatter={tooltipFormatter} />
            </RadarChart>
          </ResponsiveContainer>
        );

      case 'combo':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dataArray} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" opacity={0.5} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip formatter={tooltipFormatter} />
              <Legend />
              <Bar yAxisId="left" dataKey="value" fill={COLORS[0]} />
              <Line yAxisId="right" type="monotone" dataKey="value" stroke={COLORS[1]} strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        );

      case 'table':
        return (
          <div className="h-full overflow-auto">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {dataArray.length > 0 && Object.keys(dataArray[0]).filter(k => k !== 'fullName').slice(0, 10).map((col) => (
                        <th
                          key={col}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dataArray.slice(0, 100).map((row: any, index: number) => (
                      <tr key={index} className="hover:bg-gray-50">
                        {Object.keys(row).filter(k => k !== 'fullName').slice(0, 10).map((col) => {
                          const value = row[col];
                          const displayValue = value !== null && value !== undefined
                            ? (typeof value === 'object' ? JSON.stringify(value) : String(value))
                            : 'N/A';
                          const truncated = displayValue.length > 50
                            ? displayValue.substring(0, 50) + '...'
                            : displayValue;

                          return (
                            <td
                              key={col}
                              className="px-4 py-3 text-sm text-gray-900 max-w-xs"
                              title={displayValue}
                            >
                              <div className="truncate">{truncated}</div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {dataArray.length > 100 && (
                <div className="px-4 py-3 bg-gray-50 text-center text-sm text-gray-600">
                  Showing 100 of {dataArray.length.toLocaleString()} results
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const filteredCharts = charts.filter((chart) => {
    const matchesSearch =
      chart.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chart.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chart.indexName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chart.metadata?.tags?.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      );
    const matchesType = selectedType === 'all' || chart.type === selectedType;
    return matchesSearch && matchesType;
  });

  const chartTypes = Array.from(new Set(charts.map((c) => c.type)));

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 relative overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Header Section */}
        <section className="relative overflow-hidden bg-white border-b border-emerald-200 shadow-sm">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2310b981' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          ></div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/analytics')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-700"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    My Saved Charts
                  </h1>
                  <p className="text-gray-600">
                    Manage and use your previous visualizations ({charts.length} total)
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/search')}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-emerald-500/50"
              >
                <Sparkles className="w-4 h-4" />
                Create a chart
              </button>
            </div>

            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-lg">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, description, index or tags..."
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900 placeholder-gray-400"
                  />
                </div>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900 appearance-none"
                    style={{ colorScheme: 'light' }}
                  >
                    <option value="all" className="bg-white">All types</option>
                    {chartTypes.map((type) => (
                      <option key={type} value={type} className="bg-white">
                        {getChartTypeLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content - Similar to Search.tsx step 4 */}
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
                <p className="text-gray-700">Loading your charts...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={fetchCharts}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Try again
              </button>
            </div>
          ) : filteredCharts.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center shadow-lg">
              <Bookmark className="w-20 h-20 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                {charts.length === 0 ? 'No charts created' : 'No results'}
              </h3>
              <p className="text-gray-600 mb-6">
                {charts.length === 0
                  ? 'Create your first chart to start visualizing your data'
                  : 'Try changing your search criteria'}
              </p>
              <button
                onClick={() => navigate('/search')}
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-emerald-500/50"
              >
                {charts.length === 0 ? 'Create my first chart' : 'Create a chart'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredCharts.map((chart) => {
                const category = getCategory(chart);
                const categoryColors: { [key: string]: string } = {
                  'Finance': 'bg-blue-100 text-blue-700 border-blue-300',
                  'Users': 'bg-green-100 text-green-700 border-green-300',
                  'Sales': 'bg-purple-100 text-purple-700 border-purple-300',
                  'Marketing': 'bg-pink-100 text-pink-700 border-pink-300',
                  'Analytics': 'bg-cyan-100 text-cyan-700 border-cyan-300',
                  'Analysis': 'bg-orange-100 text-orange-700 border-orange-300',
                };
                const categoryColor = categoryColors[category] || 'bg-gray-100 text-gray-700 border-gray-300';

                return (
                  <div
                    key={chart.id}
                    className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-emerald-400 transition-all shadow-lg hover:shadow-xl cursor-pointer"
                    onClick={() => handleViewChart(chart.id)}
                  >
                    {/* Chart Type Badge + Unit */}
                    <div className="px-4 pt-4 pb-2 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-200">
                        {getChartIcon(chart.type)}
                        {getChartTypeLabel(chart.type)}
                      </span>
                      {chart.dataConfig?.unit && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-md border border-amber-200" title="Measurement unit">
                          {chart.dataConfig.unit}
                        </span>
                      )}
                    </div>

                    {/* Chart Preview */}
                    <div className="h-40 px-4 py-3 bg-gray-50">
                      <div className="w-full h-full">
                        {renderChartPreview(chart)}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="px-4 pb-4 space-y-3">
                      {/* Title */}
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1" title={chart.name}>
                          {chart.name}
                        </h3>
                        {chart.description && (
                          <p className="text-xs text-gray-600 line-clamp-2">
                            {chart.description}
                          </p>
                        )}
                      </div>

                      {/* Category & Date */}
                      <div className="flex items-center justify-between text-xs">
                        <span className={`px-2 py-0.5 rounded-full border ${categoryColor}`}>
                          • {category}
                        </span>
                        {chart.metadata?.createdAt && (
                          <span className="text-gray-500">
                            {formatDate(chart.metadata.createdAt)}
                          </span>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewChart(chart.id);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900"
                          title="View"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditChart(chart.id);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900"
                          title="Edit"
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateChart(chart.id, chart.name);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900"
                          title="Duplicate"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteChart(chart.id);
                          }}
                          className="p-1.5 hover:bg-red-50 rounded-md transition-colors text-red-600 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal pour afficher le graphique réel */}
      {isModalOpen && selectedChartId && (() => {
        const selectedChart = charts.find(c => c.id === selectedChartId);
        if (!selectedChart) return null;
        
        const chart = selectedChart;
        
        const chartDataState = chartsData.get(selectedChartId);
        const chartData = chartDataState?.chartData || [];
        const isLoading = chartDataState?.loading ?? false;
        const chartError = chartDataState?.error;
        const totalResults = chartDataState?.totalResults || 0;

        return (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-4 flex-1">
                  <div className={`p-3 bg-gradient-to-br ${getChartColor(chart.type)} rounded-xl text-white shadow-lg`}>
                    {getChartIcon(chart.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">{chart.name}</h2>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                        {getChartTypeLabel(chart.type)}
                      </span>
                      {chart.dataConfig?.xAxis?.field && chart.dataConfig?.yAxis?.field && (
                        <span>
                          {chart.dataConfig.xAxis.label || chart.dataConfig.xAxis.field} ×{' '}
                          {chart.dataConfig.yAxis.label || chart.dataConfig.yAxis.field}
                        </span>
                      )}
                      {totalResults > 0 && (
                        <span>({totalResults.toLocaleString()} results)</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (chart) fetchChartData(chart);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-md transition-colors text-gray-700"
                    title="Refresh"
                  >
                    <RefreshCw className="h-5 w-5" />
                  </button>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-gray-100 rounded-md transition-colors text-gray-700"
                    title="Close"
                  >
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-auto p-6">
                {chart.description && (
                  <p className="text-gray-700 mb-4">{chart.description}</p>
                )}
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Database className="w-4 h-4" />
                    {chart.indexName}
                  </span>
                  {chart.metadata?.createdAt && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(chart.metadata.createdAt).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Chart Display */}
                <div className="h-[600px] border border-gray-200 rounded-xl p-4 bg-gray-50">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="flex flex-col items-center space-y-4">
                        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
                        <p className="text-gray-700">Loading data...</p>
                      </div>
                    </div>
                  ) : chartError ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <p className="text-red-700 mb-4">{chartError}</p>
                        <button
                          onClick={() => {
                            if (chart) fetchChartData(chart);
                          }}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                        >
                          Try again
                        </button>
                      </div>
                    </div>
                  ) : (
                    renderChart(chart, chartData)
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap');
      `}</style>
    </div>
  );
};

export default MyCharts;
